import { SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Alert, Divider, Form, message, Modal, Space, Spin, Typography } from "antd";
import { useUpdateElectricalConfigurationMutation } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useEffect, useState } from "react";
import { ElectricalConfigurationInput } from "../../types/electricalConfig";
import {
  formatElectricalConfigForDisplay,
  isElectricalConfigComplete,
  validateElectricalConfig,
} from "../../utils/electricalConfig";
import ElectricalConfigForm from "./ElectricalConfigForm";

const { Text } = Typography;

interface ElectricalConfigModalProps {
  visible: boolean;
  onClose: () => void;
  selectedPositions: string[];
  onRefetch?: () => void;
  loading?: boolean;
}

const ElectricalConfigModal: React.FC<ElectricalConfigModalProps> = ({
  visible,
  onClose,
  selectedPositions,
  onRefetch,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [electricalConfig, setElectricalConfig] = useState<ElectricalConfigurationInput>({});

  const [updateElectricalConfig, { loading: updateLoading }] = useUpdateElectricalConfigurationMutation({
    onCompleted: () => {
      message.success(`Electrical configuration updated successfully for ${selectedPositions.length} position(s)`);
      onRefetch?.();
      onClose();
    },
    onError: (error) => {
      message.error(`Failed to update electrical configuration: ${error.message}`);
    },
  });

  useEffect(() => {
    if (visible) {
      // Don't automatically reset fields - let parent provide proper initial values
      setElectricalConfig({});
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    try {
      if (selectedPositions.length === 0) {
        message.error("Please select at least one position to configure");
        return;
      }

      // First validate the form to check for errors
      try {
        await form.validateFields();
      } catch (validationError) {
        // If validation fails, don't proceed
        return;
      }

      // Get all current form values (including those that might not pass validation in edit mode)
      const allFormValues = form.getFieldsValue();

      // Filter out undefined, null, empty string, and zero values but be more selective
      const cleanedFormValues = Object.fromEntries(
        Object.entries(allFormValues).filter(([, value]) => {
          // Keep the value if it's a valid number (including 0) or non-empty string
          if (typeof value === "number") {
            return !Number.isNaN(value) && Number.isFinite(value);
          }
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          // For other types, exclude null/undefined
          return value !== undefined && value !== null;
        })
      );

      // Only proceed if there are actual values to update
      if (Object.keys(cleanedFormValues).length === 0) {
        message.error("Please provide at least one electrical configuration value to update");
        return;
      }

      const validation = validateElectricalConfig(cleanedFormValues);

      if (!validation.isValid) {
        message.error(`Configuration errors: ${validation.errors.join(", ")}`);
        return;
      }

      // Update all positions with the cleaned electrical configuration
      await updateElectricalConfig({
        variables: {
          positionIds: selectedPositions,
          electricalConfig: cleanedFormValues,
        },
      });
    } catch (error: any) {
      message.error(`Failed to update electrical configuration: ${error.message}`);
    }
  };

  const handleValuesChange = (changedValues: any, allValues: ElectricalConfigurationInput) => {
    setElectricalConfig(allValues);
  };

  // Get position names for display (simplified for now since we don't have position data)
  const getSelectedPositionNames = () => {
    return selectedPositions.map((id) => `Position ${id.slice(-8)}`).join(", ");
  };

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined />
          <span>Configure Electrical Parameters</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      width={800}
      confirmLoading={updateLoading}
      okText="Apply Configuration"
      cancelText="Cancel"
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div style={{ marginBottom: 24 }}>
          <Alert
            message="Electrical Configuration"
            description="Configure electrical parameters for energy monitoring positions. These settings affect power calculations and measurement accuracy."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {selectedPositions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Configuring {selectedPositions.length} position(s)</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Position IDs: {getSelectedPositionNames()}</Text>
              </div>
            </div>
          )}

          <Divider>
            <Space>
              <SettingOutlined />
              <span>Electrical Parameters</span>
            </Space>
          </Divider>

          <ElectricalConfigForm form={form} onValuesChange={handleValuesChange} showAdvanced compact={false} editMode />

          {Object.keys(electricalConfig).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Alert
                message="Configuration Preview"
                description={formatElectricalConfigForDisplay(electricalConfig)}
                type={isElectricalConfigComplete(electricalConfig) ? "success" : "warning"}
                showIcon
              />
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ElectricalConfigModal;
